import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { Theme } from '@mui/material';
import { Box, Typography } from '@mui/material';
import type { ComponentProps } from 'react';
import { combineSx } from '@/utils/combineSx';
import { pxToRem } from '@/utils/pxToRem';
import { Label } from '../Label';
import { MAndAIcon } from './MAndAIcon';
import type { CompanyParticipation, Metric as MetricType } from './types';

const metricStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    gap: pxToRem(5),
    position: 'relative',
  },
  value: (theme: Theme) => ({
    fontSize: theme.fontSizes.smaller,
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.25,
    fontVariantNumeric: 'tabular-nums',
    textWrap: 'balance',
    wordBreak: 'keep-all',
    padding: `0 ${pxToRem(1)}`,
  }),
  unit: (theme: Theme) => ({
    fontSize: theme.fontSizes.smallest,
    fontWeight: '400',
    color: theme.palette.baseColors.deepSeaNavy.c100,
    padding: '0.125rem',
    display: 'inline-block',
    lineHeight: '1',
    verticalAlign: 'text-bottom',
  }),
  labelLink: {
    position: 'relative',
    textDecoration: 'none',
    display: 'inline',
    alignSelf: 'flex-start',
    '&:hover': {
      textDecoration: 'underline',
      '& svg': {
        opacity: 1,
        transition: 'opacity var(--ease-move) 0.2s',
      },
    },

    '& svg': {
      position: 'absolute',
      left: '0',
      top: '0',
      translate: '-100% 50%',
      fontSize: pxToRem(12),
      opacity: 0,
      transition: 'opacity var(--ease-exit) 0.1s 0.2s',
    },
  },
};

const isCompanyParticipationArray = (
  value: MetricType['value'],
): value is CompanyParticipation[] => {
  return Array.isArray(value);
};

const Link = ({ href, sx, children }: ComponentProps<typeof Typography> & { href: string }) => {
  return (
    <Typography
      sx={combineSx(metricStyles.labelLink, sx)}
      component='a'
      target='_blank'
      href={href}
    >
      {children}
      <OpenInNewIcon />
    </Typography>
  );
};

const Participation = ({ participation }: { participation: CompanyParticipation }) => {
  return (
    <Box
      sx={combineSx(metricStyles.value, {
        display: 'grid',
        gridTemplateColumns: `auto 1fr auto`,
        gridAutoFlow: 'column',
        gridAutoColumns: `minmax(min-content, ${pxToRem(32)})`,
        placeItems: 'end stretch',
        width: '100%',
        gap: pxToRem(2),
      })}
    >
      {participation.link ? (
        <Link href={participation.link} sx={metricStyles.value}>
          {participation.company}
        </Link>
      ) : (
        <Typography sx={metricStyles.value}>{participation.company}</Typography>
      )}
      <Box
        component='i'
        sx={{
          borderBottom: 'dotted rgba(0, 0, 0, 0.25) 2px',
          height: '60%',
          mb: '0.25rem',
        }}
      />
      <Box
        sx={{ fontSize: pxToRem(13), opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}
        component='b'
      >
        {' '}
        {participation.formattedPercentStake}
      </Box>
      <MAndAIcon
        enabled={participation.needsIcon}
        sx={{ placeSelf: 'end', marginLeft: '0', px: '0.2rem' }}
      />
    </Box>
  );
};

const Unit = ({ unit }: { unit: string | undefined }) => {
  if (!unit) {
    return null;
  }
  return (
    <Typography component='sub' sx={metricStyles.unit}>
      {unit}
    </Typography>
  );
};

const Value = ({ value, unit, link }: Omit<MetricType, 'label'>) => {
  if (!value) {
    return <Typography>-</Typography>;
  }

  if (isCompanyParticipationArray(value)) {
    return (
      <Box
        sx={combineSx(metricStyles.value, {
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: pxToRem(4),
        })}
      >
        {value.map(participation => (
          <Participation participation={participation} key={participation.company} />
        ))}
      </Box>
    );
  }

  if (link) {
    return (
      <Link href={link} sx={metricStyles.value}>
        {value}
        <Unit unit={unit} />
      </Link>
    );
  }

  return (
    <Typography sx={metricStyles.value}>
      {value}
      <Unit unit={unit} />
    </Typography>
  );
};

type MetricProps = MetricType & ComponentProps<typeof Box>;

const Metric = ({ label, value, link, unit, sx }: MetricProps) => {
  const hasValue = !!value;

  return (
    <Box
      sx={combineSx(
        metricStyles.container,
        {
          background: 'rgba(0, 0, 0, 0)',
          opacity: hasValue ? 1 : 0.15,
        },
        sx,
      )}
      data-id='metric'
    >
      <Label label={label} />
      <Value value={value || ''} unit={unit || ''} link={link || null} />
    </Box>
  );
};

export { Metric };
